import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { type ColDef, type ICellRendererParams } from "ag-grid-community";
import {
  Add01Icon,
  Delete01Icon,
  InformationCircleIcon,
  PencilEdit02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { Card } from "shared/components/Card";
import { AgGrid } from "shared/components/AgGrid";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { useAuthContext } from "providers/Auth";
import {
  AUTH_TYPE_BY_VENDOR,
  getVendorAccountErrorMessage,
  lockboxAccountsApi,
  VendorAccount
} from "api/lockbox-accounts";
import type { LockboxIntegratedVendor } from "api/landlord/lockboxes/types";
import { VENDOR_LABELS } from "views/lockboxes/constants";
import { AddLockboxAccountModal } from "./AddLockboxAccountModal";
import { RenameLockboxAccountModal } from "./RenameLockboxAccountModal";

interface LockboxAccountsListProps {
  vendor: LockboxIntegratedVendor;
  // Swaps in a vendor-specific "add account" trigger (e.g. Igloohome's OAuth flow)
  // while still reusing this list/rename/delete grid.
  renderAddAccount?: (props: {
    existingAccounts: VendorAccount[];
  }) => ReactNode;
}

const MASKED_PASSWORD = "*".repeat(10);

const EMPTY_ACCOUNTS: VendorAccount[] = [];

const CANNOT_BE_UNDONE = "This action cannot be undone.";

const disconnectDescription = ({ name, lockbox_count }: VendorAccount) => {
  if (lockbox_count === 0) return CANNOT_BE_UNDONE;

  const noun = lockbox_count === 1 ? "lockbox" : "lockboxes";
  return `${lockbox_count} ${noun} assigned to ${name} will become unassigned. ${CANNOT_BE_UNDONE}`;
};

export const LockboxAccountsList = ({
  vendor,
  renderAddAccount
}: LockboxAccountsListProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refreshPartnerData } = useAuthContext();
  const isOAuth = AUTH_TYPE_BY_VENDOR[vendor] === "oauth";

  const [addOpen, setAddOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<VendorAccount | null>(null);
  const [disconnectTarget, setDisconnectTarget] =
    useState<VendorAccount | null>(null);

  const {
    data: accounts = EMPTY_ACCOUNTS,
    isLoading,
    isError
  } = useQuery({
    queryKey: ["LOCKBOX_ACCOUNTS", vendor],
    queryFn: () => lockboxAccountsApi.list(vendor)
  });

  const { mutate: disconnectAccount, isPending: isDisconnecting } = useMutation(
    {
      mutationKey: ["LOCKBOX_ACCOUNTS", vendor, "DELETE"],
      mutationFn: (id: number) => lockboxAccountsApi.remove(vendor, id),
      onSuccess: () => {
        toast.success("Credentials deleted");
        queryClient.invalidateQueries({
          queryKey: ["LOCKBOX_ACCOUNTS", vendor]
        });
        queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
        refreshPartnerData();
        setDisconnectTarget(null);
        if (accounts.length === 1) navigate("/settings/integration");
      },
      onError: (err) => {
        toast.error(getVendorAccountErrorMessage(err));
      }
    }
  );

  const columns = useMemo<ColDef<VendorAccount>[]>(() => {
    const nameColumn: ColDef<VendorAccount> = {
      headerName: "Name",
      colId: "name",
      flex: 1,
      minWidth: 160,
      cellRenderer: (params: ICellRendererParams<VendorAccount>) => (
        <p className="truncate text-sm font-medium text-content-1000">
          {params.data?.name}
        </p>
      )
    };

    const actionsColumn: ColDef<VendorAccount> = {
      headerName: "",
      colId: "actions",
      width: 136,
      cellRenderer: (params: ICellRendererParams<VendorAccount>) => {
        const account = params.data;
        if (!account) return null;
        return (
          <div className="flex items-center justify-end gap-2">
            <IconButton
              variant={IconButtonVariant.BASE}
              size={IconButtonSize.SM}
              HugeIcon={PencilEdit02Icon}
              aria-label={`Rename ${account.name}`}
              tooltip="Rename"
              className="!text-content-1000"
              onClick={() => setRenameTarget(account)}
            />
            <IconButton
              variant={IconButtonVariant.BASE}
              size={IconButtonSize.SM}
              HugeIcon={Delete01Icon}
              aria-label={`Delete ${account.name}`}
              tooltip="Delete"
              className="!text-content-1000"
              onClick={() => setDisconnectTarget(account)}
            />
          </div>
        );
      }
    };

    // OAuth-based accounts have no username/password to show.
    if (isOAuth)
      return [
        nameColumn,
        {
          headerName: "Company",
          colId: "company",
          flex: 4,
          minWidth: 200,
          cellRenderer: (params: ICellRendererParams<VendorAccount>) => {
            const companyName = params.data?.metadata?.company_name;
            return (
              <p className="text-sm text-content-400">
                {typeof companyName === "string" && companyName
                  ? companyName
                  : "-"}
              </p>
            );
          }
        },
        actionsColumn
      ];

    return [
      nameColumn,
      {
        headerName: "Username",
        colId: "username",
        flex: 2,
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams<VendorAccount>) => (
          <p className="truncate text-sm text-content-400">
            {params.data?.username}
          </p>
        )
      },
      {
        headerName: "Password",
        colId: "password",
        flex: 2,
        minWidth: 200,
        cellRenderer: () => (
          <p className="text-sm tracking-widest text-content-400">
            {MASKED_PASSWORD}
          </p>
        )
      },
      actionsColumn
    ];
  }, [isOAuth]);

  return (
    <Card className="w-full p-0 mt-4">
      <div className="flex items-center justify-between p-6">
        <h3 className="text-base font-bold text-content-1000">Credentials</h3>
        {renderAddAccount ? (
          renderAddAccount({ existingAccounts: accounts })
        ) : (
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            HugeIcon={Add01Icon}
            onClick={() => setAddOpen(true)}
          >
            Add credentials
          </Button>
        )}
      </div>

      <Show if={isError}>
        <p className="px-6 pb-6 text-sm text-danger-500">
          Unable to load credentials. Please try again later.
        </p>
      </Show>

      <Show if={!isError}>
        <AgGrid<VendorAccount>
          columns={columns}
          rows={accounts}
          loading={isLoading}
          wrapperClassName="!rounded-none !border-x-0 !border-b-0 !border-t !border-alpha-40"
          themeOverrides={{ "--ag-cell-horizontal-padding": "24px" }}
          getRowId={(params) => String(params.data.id)}
          noRowsFallback={
            <NoRowsFallback
              title="No credentials yet"
              text={
                isOAuth
                  ? `Connect a ${VENDOR_LABELS[vendor]} account to start assigning lockboxes to it.`
                  : `Add a ${VENDOR_LABELS[vendor]} login to start assigning lockboxes to it.`
              }
            />
          }
        />
      </Show>

      <Show if={!renderAddAccount}>
        <AddLockboxAccountModal
          vendor={vendor}
          open={addOpen}
          existingAccounts={accounts}
          onClose={() => setAddOpen(false)}
          onSuccess={() => setAddOpen(false)}
        />
      </Show>

      <RenameLockboxAccountModal
        key={renameTarget?.id ?? "none"}
        vendor={vendor}
        account={renameTarget}
        existingAccounts={accounts}
        onClose={() => setRenameTarget(null)}
      />

      <ConfirmDialog
        opened={Boolean(disconnectTarget)}
        onClose={() => {
          if (!isDisconnecting) setDisconnectTarget(null);
        }}
        onConfirm={() => {
          if (disconnectTarget) disconnectAccount(disconnectTarget.id);
        }}
        title={
          disconnectTarget
            ? `Are you sure you want to delete ${disconnectTarget.name}?`
            : ""
        }
        description={
          disconnectTarget ? disconnectDescription(disconnectTarget) : undefined
        }
        variant="danger"
        icon={{ icon: InformationCircleIcon, variant: "danger" }}
        confirmButton={{ label: "Delete", loading: isDisconnecting }}
        cancelButton={{ label: "Cancel", disabled: isDisconnecting }}
      />
    </Card>
  );
};
